import { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { DBAccount } from "../../../../api/types";
import { useAPI } from "../../../../api/useAPI";

interface Props {
  open: boolean;
  handleClose: () => void;
  account: DBAccount;
  owners: string[];
  reloadAccounts: () => void;
}

/**
 * Set who an account belongs to. Accounts with an owner are listed under
 * "Personal"; clearing the owner moves the account to "Testing".
 */
export default function OwnerModal({
  open,
  handleClose,
  account,
  owners,
  reloadAccounts,
}: Props) {
  const { setAccountOwner } = useAPI();
  const [owner, setOwner] = useState(account.owner ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setOwner(account.owner ?? "");
      setError("");
    }
  }, [open, account.owner]);

  const save = async (value: string | null) => {
    setSaving(true);
    setError("");
    try {
      const result = await setAccountOwner(account._id.toString(), value);
      if (!result.success) {
        setError(result.error || "Failed to update owner");
        return;
      }
      reloadAccounts();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Account owner</DialogTitle>
      <DialogContent>
        <Typography color="textSecondary" sx={{ mb: 2 }}>
          Accounts with an owner are shown under "Personal". Without an owner
          the account is shown under "Testing".
        </Typography>
        <Autocomplete
          freeSolo
          options={owners}
          inputValue={owner}
          onInputChange={(_, value) => setOwner(value)}
          renderInput={(params) => (
            <TextField {...params} autoFocus label="Owner" />
          )}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {account.owner && (
          <Button
            color="warning"
            disabled={saving}
            onClick={() => save(null)}
            sx={{ mr: "auto" }}
          >
            Clear owner
          </Button>
        )}
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving || !owner.trim()}
          onClick={() => save(owner.trim())}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
